<template lang="pug">
  div
    bionic-metrics-page-loading(v-if='loading')
    bionic-metrics-no-data(v-if='!loading && (!testRuns || !testRuns.length)')
    v-container(fluid grid-list-md v-if='!loading && testRuns')
      v-row(wrap)
        v-col(v-for='(testRun, testRunIndex) in testRuns' :key='testRun.guid' cols='12')
          v-card.testRun-card
            v-card-title
              v-row(no-gutters)
                v-col(cols='8' sm='8' md='9')
                  .text-h5.font-weight-light(@click.right.stop.prevent='to(sourceUrl)') {{sourceName}}
                    bionic-metrics-browser.ml-1(:environment='testRun.environment')
                  .text-subtitle-2.font-weight-light {{testRun.baseUrl}}
                  .text-subtitle-2.font-weight-light(v-if='testRun.targetVersion') Version: {{testRun.targetVersion}}
                v-col(cols='4' sm='4' md='3')
                  v-row(justify='end' align='start').mr-2
                    bionic-metrics-test-status-indicator(v-if='testRun.status && !testRun.isRunning', :status='testRun.status' :size='iconSize').mr-1
                    bionic-metrics-running-indicator-circular(v-if='testRun.isRunning' :testSuite='testSuite' @cancelled='onCancelled' :size='runningIconSize').mr-1
                    bionic-metrics-video-player.ml-1(v-if='hasVideo && mediaReady(testRun) && !hasPerTestVideo', :testRun='testRun' :size='iconSize').mr-1
                    bionic-metrics-screens-carousel(v-if='hasDefaultScreens && mediaReady(testRun)', :testRun='testRun' ref='carousel' :size='iconSize')
            v-expansion-panels(accordion focusable multiple v-model='panels')
              v-expansion-panel(v-for='(test, index) in testRun.tests', :key='index' :id='`panel-${testRunIndex}-${index}`')
                v-expansion-panel-header.pt-0.pb-0.pr-2.pl-2
                  template(v-slot:actions)
                    v-icon(:size='iconSize') keyboard_arrow_down
                  v-row(no-gutters align='center').ma-0.pa-0
                    v-col(no-gutters cols='12' sm='8' md='9')
                      .text-h6.font-weight-light(@click.right.stop.prevent='to(`/test/${test.guid}`)') {{test.name}}
                    v-col(no-gutters cols='12' sm='4' md='3')
                      v-row(no-gutters :justify='`${smallScreen ? "start" : "end"}`' align='center')
                        bionic-metrics-browser-logs-button(v-if='hasBrowserLogs(test)' :test='test' :size='iconSize' @click.stop.prevent='goToBrowserLogs(index)')
                        bionic-metrics-video-player(v-if='hasPerTestVideo && hasMedia(test)' :testRun='testRun' :index='index' :size='iconSize').ml-1.mr-1
                        bionic-metrics-screens-button(v-if='hasDefaultScreens && hasMedia(test)' :test='test' :size='iconSize' @click.stop.prevent='goToScreens(index)')
                        bionic-metrics-test-status-indicator.ml-1.mr-2(v-if='!test.isRunning' :status='test.status' :size='iconSize')
                        bionic-metrics-running-indicator-circular.ml-1.mr-2(v-if='test.isRunning' :size='runningIconSize')
                v-expansion-panel-content
                  bionic-metrics-test-breakdown(:id='`test-breakdown-${index}`' v-if='test.startUs && panels.includes(index)' :test='test')
                  bionic-metrics-test-logs(:id='`test-logs-${index}`' :test='test')
                  bionic-metrics-browser-logs(:id='`browser-logs-${index}`' :test='test')
                  bionic-metrics-error(:id='`test-error-${index}`' :test-or-test-run='test')
                  v-divider.mt-4
                  bionic-metrics-screens(:id='`screens-${index}`' v-if='hasBrowser(testRun)' :testRun='testRun' :test='test' :index='index')
            v-card-text.lighten-4.logs-editor.text-caption.font-weight-light(v-if='testRun.logs' :class='logClass')
              span.text-subtitle-1.font-weight-light Top Level Logs
              div(v-for='log in testRun.logs') {{log}}
            bionic-metrics-error(:id='`test-run-error-${testRunIndex}`' v-if='testRun.error' :test-or-test-run='testRun')

</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import {mapGetters, mapActions, mapMutations} from 'vuex';
import TestSuite from '../../api/testSuite';
import Messages from '../../services/message.service';
import BionicMetricsTestStatusIndicator from '../../components/TestStatusIndicator';
import BionicMetricsVideoPlayer from '../../components/VideoPlayer';
import BionicMetricsPageLoading from '../../components/PageLoading';
import BionicMetricsNoData from '../../components/NoData';
import BionicMetricsBrowser from '../../components/Browser';
import BionicMetricsRunningIndicatorCircular from '../../components/RunningIndicatorCircular';
import BionicMetricsScreensCarousel from '../../components/ScreensCarousel';
import UtilMixin from '../../mixins/util.mixin';
import BionicMetricsScreens from '../../components/Screens';
import BionicMetricsBrowserLogsButton from '../../components/BrowserLogsButton';
import BionicMetricsScreensButton from '../../components/ScreensButton';
import BionicMetricsTestLogs from '../../components/TestLogs';
import BionicMetricsBrowserLogs from '../../components/BrowserLogs';
import BionicMetricsTestBreakdown from '../../components/TestBreakdown';
import BionicMetricsError from '../../components/ErrorDisplay';
import LoadTest from '../../api/loadTest';

export default {
  name: 'BionicMetricsTestRuns',
  components: {
    BionicMetricsError,
    BionicMetricsTestBreakdown,
    BionicMetricsBrowserLogs,
    BionicMetricsTestLogs,
    BionicMetricsScreensButton,
    BionicMetricsBrowserLogsButton,
    BionicMetricsScreens,
    BionicMetricsScreensCarousel,
    BionicMetricsRunningIndicatorCircular,
    BionicMetricsBrowser,
    BionicMetricsNoData,
    BionicMetricsPageLoading,
    BionicMetricsVideoPlayer,
    BionicMetricsTestStatusIndicator
  },
  props: ['runGuid'],
  mixins: [UtilMixin],
  computed: {
    smallScreen() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    sourceUrl() {
      if (!this.loadTest && !this.testSuite) return null;
      if (this.isLoadTest) return `/load-test/${this.loadTest.guid}`;
      return `/testsuite/${this.testSuite.guid}`;
    },
    sourceName() {
      if (!this.loadTest && !this.testSuite) return null;
      if (this.isLoadTest) return this.loadTest.name;
      return this.testSuite.name;
    },
    hasVideo() {
      if (this.isLoadTest) return false;
      return _.get(this.testRuns, '0.videoEnabled', false);
    },
    hasPerTestVideo() {
      return (this.hasVideo && this.testSuite.perTestVideo);
    },
    hasDefaultScreens() {
      return !this.isLoadTest;
    },
    logClass() {
      if (this.$vuetify.theme.dark) return 'dark';
      return 'grey';
    },
    ...mapGetters({
      testSuite: 'getTestSuite'
    })
  },
  async mounted() {
    await this.load();
  },
  async beforeDestroy() {
    await Messages.unsubscribe(this.subscribeChannels, this.messageListener, this.messageKey);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const {list, total} = await TestSuite.getTestRuns(this.runGuid);
        this.testRuns = list;
        this.total = total;
        const first = _.first(this.testRuns);
        this.isLoadTest = !!first.loadTest;
        if (!this.isLoadTest) await this.getTestSuite(first.testSuiteGuid);
        else await this.getLoadTest(first.loadTestGuid);
        this.updateBreadcrumb();
        this.messageListener = {message: this.onMessage};
        this.subscribeChannels = _.map(this.testRuns, 'guid');
        this.subscribeChannels.push(this.runGuid);
        await Messages.subscribe(this.subscribeChannels, this.messageListener, this.messageKey);
        if (!_.isUndefined(this.$route.query.index)) this.openIndex(parseInt(this.$route.query.index, 10));
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async getLoadTest(guid) {
      this.loadTest = await LoadTest.get(guid);
    },
    onCancelled() {
      for (const tr of this.testRuns) {
        tr.status = 'CANCELLED';
        tr.isRunning = false;
        tr.statusUpdatedAt = new Date();
        for (const test of tr.tests) {
          if (test.isRunning) test.isRunning = false;
        }
      }
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{
        text: this.sourceName,
        to: this.sourceUrl,
        exact: true
      }, {text: 'Result'}]);
    },
    updateStatus(runGuid, message) {
      const testRun = _.find(this.testRuns, {guid: message.testRunGuid});
      Vue.set(testRun, 'status', message.status);
      Vue.set(testRun, 'isRunning', message.isRunning);
    },
    updateTestStatus(runGuid, message) {
      const testRun = _.find(this.testRuns, {guid: message.testRunGuid});
      testRun.tests[message.testIndex].status = message.status;
      testRun.tests[message.testIndex].isRunning = message.isRunning;
      testRun.tests[message.testIndex].error = message.error;
    },
    updateLog(runGuid, message) {
      console.log(message);
      const testRun = _.find(this.testRuns, {guid: message.testRunGuid});
      if (!_.isNumber(message.index)) testRun.logs.push(message.log);
      else testRun.tests[message.index].logs.push(message.log);
    },
    onMessage(data) {
      if (_.get(data, 'message.type') === 'status') return this.updateStatus(data.channel, data.message);
      if (_.get(data, 'message.type') === 'testStatus') return this.updateTestStatus(data.channel, data.message);
      if (_.get(data, 'message.type') === 'log') return this.updateLog(data.channel, data.message);
    },
    scrollTo(id, delay) {
      if (!delay) delay = 100;
      Vue.nextTick(() => {
        setTimeout(() => this.$vuetify.goTo(id), delay);
      });
    },
    openIndex(index) {
      this.panels = [index];
      this.scrollTo(`#panel-0-${index}`);
    },
    openCarouselTo(testRunIndex, index) {
      this.$refs.carousel[testRunIndex].openTo(index);
    },
    mediaReady(testRun) {
      return this.hasBrowser(testRun) && testRun.status !== 'NO_DATA' && !testRun.isRunning;
    },
    hasBrowserLogs(test) {
      return !_.isEmpty(test.browserLogs);
    },
    hasMedia(test) {
      return test.status !== 'NO_DATA' && !test.isRunning;
    },
    hasBrowser(testRun) {
      return !testRun.noBrowser;
    },
    goToId(index, id) {
      if (!this.panels.includes(index)) {
        this.panels.push(index);
        Vue.nextTick(() => {
          setTimeout(() => this.$vuetify.goTo(id), 500);
        });
      } else {
        this.$vuetify.goTo(id);
      }
    },
    goToBrowserLogs(index) {
      this.goToId(index, `#browser-logs-${index}`);
    },
    goToScreens(index) {
      this.goToId(index, `#screens-${index}`);
    },
    ...mapActions({
      getTestSuite: 'getTestSuite'
    }),
    ...mapMutations({
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  data() {
    return {
      panels: [],
      subscribeChannels: [],
      loading: false,
      testRuns: null,
      isLoadTest: false,
      total: 0,
      iconSize: 28,
      runningIconSize: 24,
      messageListener: null,
      messageKey: 'testruns'
    };
  }
};
</script>

<style lang="scss">
.v-expansion-panel-content__wrap {
  padding: 0;
}

.error-box {
  padding: 2em;
  overflow-wrap: break-word;
}

.tight-button {
  margin: 0;
  padding: 0;
}

.action-item {
  margin: 5px;
}

.logs-editor {
  white-space: pre;
  overflow: auto;
}

.testRun-card {
  height: 100%;
  margin-bottom: -6px;
  padding-bottom: 0;
}

.dark {
  background-color: #222222;
}
</style>


<!--TODO: log streaming and index based open-->
